<template>
    <b-tabs
        class="internship-project-overview-tab apprentice-tab"
        v-model="activeTab"
        content-class="internship-project-overview-content"
        @input="updateTabIndex">
        <template #tabs-end>
            <div class="apprentice-tab-buttons internship-project-overview-tab-header">
                <b-badge
                    class="badge-rating internship-report-rating"
                    v-if="hasRatingResult && activeTab === 1"
                    pill
                    variant="primary">
                    <i class="fas fa-fw fa-trophy"></i>{{ getResultValue }}
                </b-badge>
                <design-2021-internship-report-show-open-close-component
                    :group-in="groupIn"
                    :project-in="dynamicProject">
                </design-2021-internship-report-show-open-close-component>

                <b-button
                    class="btn-medal btn-rate-portfolio-show"
                    v-if="portfolio.portfolio_rating"
                    v-b-modal="'portfolio-rating'"
                    variant="primary-icon">
                </b-button>
            </div>

            <design-2021-internship-report-show-rating-modal-component
                v-if="canRate"
                :rating-in="portfolio.portfolio_rating"
                key-name="portfolio-rating"
                :children-in="portfolio.categories"
                :child-title="$t('internship.category_results')"
                :title="$t('internship.portfolio_rating')"
                @save-rating="savePortfolioRating($event)">
            </design-2021-internship-report-show-rating-modal-component>
        </template>

        <b-tab
            :title="$t('internship.exercises')"
            title-item-class="assignments-tab">
            <design-2021-internship-report-show-assignments-component
                :project-in="projectIn"
                :portfolio-in="portfolioIn"
                :project-id="dynamicProject.id"
                :category-id="categoryIdIn">
            </design-2021-internship-report-show-assignments-component>
        </b-tab>

        <b-tab
            :title="$t('internship.internship')"
            title-item-class="internship-tab">
            <design-2021-internship-report-show-internship-component
                @signUp="signUp($event)"
                :group-in="groupIn"
                :project-in="dynamicProject"
                :google-map-api="googleMapApi"
                @show-assignments="updateTabIndex(0)">
            </design-2021-internship-report-show-internship-component>
        </b-tab>

        <b-tab
            title="Tools"
            title-item-class="tools-tab">
            <design-2021-internship-report-show-tools-component
                :project-in="dynamicProject"
                :group-in="groupIn"
                :logs-in="logsIn"
                :logs-by-time-in="logsByTimeIn"
                :questionnaire-in="questionnaireIn"
                :ratings-in="ratingsIn"
                :agenda-items-in="agendaItemsIn"
                :agenda-title-in="agendaTitleIn">
            </design-2021-internship-report-show-tools-component>
        </b-tab>

        <b-tab
            :title="$t('internship.messages')"
            title-item-class="messages-tab">
            <template v-slot:title>
                {{ $t('internship.messages') }}
                <span
                    class="notification-bubble paper-messages-count messages-count-bubble"
                    v-if="count !== 0">
                    {{ count }}
                </span>
            </template>

            <design-2021-project-show-message-component
                :project-type-id="dynamicProject.id"
                :messages-in="messagesIn"
                message-type="project">
            </design-2021-project-show-message-component>
        </b-tab>

        <b-tab
            v-if="$authUser.isTeacher()"
            :title="$t('common.manage')"
            title-item-class="messages-tab">
            <design-2021-internship-report-show-teacher-component
                :project-in="dynamicProject"></design-2021-internship-report-show-teacher-component>
        </b-tab>

        <b-tab
            v-if="$authUser.isAdmin()"
            title="Admin"
            title-item-class="management-tab">
            <design-2021-internship-report-show-admin-component :project-in="dynamicProject">
            </design-2021-internship-report-show-admin-component>
        </b-tab>
    </b-tabs>
</template>

<script>
import { CommonBus } from '../../../app';

export default {
    name: 'design-2021-internship-report-show-component',
    props: [
        'projectIn',
        'groupIn',
        'roadmapIn',
        'questionnaireIn',
        'ratingsIn',
        'messagesIn',
        'logsIn',
        'logsByTimeIn',
        'agendaItemsIn',
        'agendaTitleIn',
        'portfolioIn',
        'categoryIdIn',
        'googleMapApi',
    ],
    data() {
        return {
            activeTab: 1,
            count: this.projectIn.group.messages_count,
            portfolio: this.portfolioIn,
            project: this.projectIn,
        };
    },
    computed: {
        dynamicProject() {
            return this.project;
        },
        canRate() {
            if (!this.portfolio) {
                return false;
            }
            return (
                !this.$authUser.isStudent() && this.portfolio.portfolio_rating && this.portfolio.portfolio_rating.type
            );
        },
        hasRatingResult() {
            if (!this.portfolio) {
                return false;
            }
            return this.portfolio.portfolio_rating && this.portfolio.portfolio_rating.results.length;
        },

        getResultValue() {
            if (!this.portfolio) {
                return null;
            }

            if (!this.portfolio.portfolio_rating || !this.portfolio.portfolio_rating.results.length) {
                return null;
            }

            if (this.portfolio.portfolio_rating.type === 1) {
                const id = this.portfolio.portfolio_rating.results[0].portfolio_rating_option_id;
                for (const option of this.portfolio.portfolio_rating.options) {
                    if (option.id === id) {
                        return option.value;
                    }
                }
            }

            if (this.portfolio.portfolio_rating.type === 2) {
                return this.portfolio.portfolio_rating.results[0].value;
            }

            return null;
        },
    },
    methods: {
        signUp(project) {
            this.$emit('signedUp', project);
            this.project.internship = project.internship;
        },
        getActiveTab() {
            let hasDeadlines = false;
            let hasAssignmentInProgress = false;

            if (this.portfolio && this.portfolio.categories.length > 0) {
                for (const category of this.portfolio.categories) {
                    if (category.assignments.length > 0) {
                        for (const assignment of category.assignments) {
                            if (assignment.deadlines > 0 && !hasDeadlines) {
                                hasDeadlines = true;
                            }

                            if (assignment.in_progress && !hasAssignmentInProgress) {
                                hasAssignmentInProgress = true;
                            }
                        }
                    }
                }
            }

            if (!this.roadmapIn && !hasDeadlines && !hasAssignmentInProgress) {
                return 1;
            }

            return 0;
        },
        resetUnreadMessages() {
            this.axios.delete(this.routes.portfolioDeleteUnreadMessage(this.projectIn.id)).then(
                (response) => {
                    this.count = 0;
                },
                (error) => {
                    this.alert.ajaxError(error);
                },
            );
        },

        updateTabIndex(tabIndex) {
            if (tabIndex === 3) {
                this.resetUnreadMessages();
            }

            if (tabIndex === 0) {
                CommonBus.$emit('initiate-slider');
            }

            this.activeTab = tabIndex;

            const name = 'internship-project-overview-active-tab-' + this.projectIn.id;
            localStorage.setItem(name, tabIndex);
        },

        savePortfolioRating(values) {
            if (!values.rating.value && !values.rating.option_id && !this.portfolio.portfolio_rating.results.length) {
                return;
            }

            this.alert.saving();

            let data = values.rating;

            data.project_id = this.project.id;

            if (!this.portfolio.portfolio_rating.results.length) {
                this.axios.post(this.routes.portfolioRatingResult(), data).then(
                    (response) => {
                        this.portfolio.portfolio_rating.results = [response.data];
                        this.alert.success();
                        // this.fireClose(); todo
                    },
                    (error) => {
                        this.alert.ajaxError(error);
                    },
                );
            } else {
                this.axios.post(this.routes.portfolioRatingResultUpdate(), data).then(
                    (response) => {
                        if (response.data) {
                            this.portfolio.portfolio_rating.results = [response.data];
                        } else {
                            this.portfolio.portfolio_rating.results = [];
                        }

                        this.alert.success();
                        // if (!this.assignment.statuses || !this.assignment.statuses.length || this.assignment.statuses[0].status !== 1) {
                        //     this.fireClose();
                        // } else {
                        //     this.alert.success();
                        // } todo
                    },
                    (error) => {
                        this.alert.ajaxError(error);
                    },
                );
            }
        },
    },

    created() {
        this.count = this.count + this.projectIn.messages_count;
        const name = 'internship-project-overview-active-tab-' + this.projectIn.id;

        if (this.portfolioIn.categories.every((category) => category.id !== this.categoryId)) {
            if (this.portfolioIn.categories.length > 0) {
                this.categoryId = this.portfolioIn.categories[0].id;
            }
        }

        if (this.categoryIdIn) {
            this.activeTab = 1;
            localStorage.setItem(name, this.activeTab);
        } else {
            if (this.projectIn.type_id === 6) {
                if (this.projectIn.internship.length === 0) {
                    this.activeTab = 1;
                } else if (this.projectIn.signed_internship_agreement !== null && localStorage.getItem(name) === '1') {
                    this.activeTab = 0;
                } else {
                    this.activeTab = localStorage.getItem(name) && parseInt(localStorage.getItem(name));
                }
            } else {
                this.activeTab = localStorage.getItem(name) && parseInt(localStorage.getItem(name));
            }
        }

        if (this.activeTab === 0 && this.projectIn.type_id !== 6) {
            this.activeTab = this.getActiveTab();
        }
    },
};
</script>

<style scoped lang="scss">
.internship-project-overview-tab ::v-deep {
    .nav-tabs {
        align-items: flex-end;
        flex-wrap: wrap;

        .internship-project-overview-tab-header {
            order: 0;
            margin-bottom: map-get($grid-gutter-spacing, 'fifteen');
        }

        .assignments-tab {
            order: 1;
        }

        .internship-tab {
            order: 2;
        }

        .tools-tab {
            order: 3;
        }

        .messages-tab {
            order: 4;
        }

        .management-tab {
            order: 5;
        }

        .progress-tab {
            margin-right: 0;
        }

        .nav-item {
            border-radius: 4px;
            background-color: $primary;
            flex: 1 0 100%;

            &:first-child {
                .nav-link {
                    padding-left: map-get($grid-gutter-spacing, 'fifteen');
                }
            }

            &:not(:last-of-type) {
                margin-bottom: map-get($grid-gutter-spacing, 'fifteen');
            }
        }

        .nav-link {
            padding: map-get($grid-gutter-spacing, 'ten') 0 map-get($grid-gutter-spacing, 'ten')
                map-get($grid-gutter-spacing, 'fifteen');

            color: $white;

            &:after {
                display: none;
            }
        }

        .last-tab {
            display: flex;
            flex: 1;
            margin-bottom: map-get($grid-gutter-spacing, 'fifteen');
        }
    }
}

@media (min-width: map-get($grid-breakpoints, 'md')) {
    .internship-project-overview-tab ::v-deep {
        .nav-tabs {
            align-items: baseline;
            flex-wrap: nowrap;

            .internship-project-overview-tab-header {
                order: 5;
                margin-bottom: 0;
            }

            .assignments-tab {
                order: 0;
            }

            .internship-tab {
                order: 1;
            }

            .tools-tab {
                order: 2;
            }

            .messages-tab {
                order: 3;
            }

            .management-tab {
                order: 4;
            }

            .nav-item {
                border-radius: 0;
                background-color: transparent;
                flex: 0 0 auto;

                &:not(:last-of-type) {
                    margin-bottom: 0;
                }

                &:first-child {
                    padding-left: 0;
                }
            }

            .nav-link {
                padding: 0 map-get($grid-gutter-spacing, 'fifteen');
                color: $primary;

                &:after {
                    display: block;
                }
            }

            .last-tab {
                margin-bottom: 0;
                align-self: center;
            }
        }
    }
}

//
//.internship-project-overview-tab-header {
//    align-self: center;
//}
//
//.btn-rate-internship-project-overview {
//    font-size: 1.125rem;
//    height: 37px;
//
//    &:before {
//        margin-right: 0;
//    }
//}
//
//.internship-report-rating {
//    font-size: 1.125rem;
//    padding-top: map-get($grid-gutter-spacing, 'eight');
//    padding-bottom: map-get($grid-gutter-spacing, 'eight');
//    height: 37px;
//
//    &,
//    i {
//        margin-right: map-get($grid-gutter-spacing, 'fifteen');
//    }
//}
//
//.internship-project-overview-nav ::v-deep {
//    &.show {
//        button.dropdown-toggle {
//            background-color: $primary;
//            color: $white;
//        }
//    }
//
//    .dropdown-menu {
//        border: 1px solid $border-color;
//
//        li.btn-rate-internship-report,
//        li.btn-internship-report-order {
//            &:before {
//                color: $primary;
//            }
//        }
//    }
//
//}
</style>
